import { useEffect, useId, useState } from 'react';
import { autocomplete, type PandoraItemKey } from '../../api/pandora';
import type { ConditionRow } from '../../api/pandoraQuery';

export const OPERATORS: { value: string; label: string }[] = [
  { value: '=', label: 'contains' },
  { value: '==', label: 'is' },
  { value: '^', label: 'starts with' },
  { value: '$', label: 'ends with' },
  { value: '<', label: '<' },
  { value: '<=', label: '≤' },
  { value: '>', label: '>' },
  { value: '>=', label: '≥' },
];

interface Props {
  row: ConditionRow;
  keys: PandoraItemKey[];
  site: string;
  onChange(row: ConditionRow): void;
  onRemove(): void;
}

/** One query-builder condition: key + operator + value (+ negate, remove). */
export default function ConditionRowEditor({ row, keys, site, onChange, onRemove }: Props) {
  const listId = useId();
  const [options, setOptions] = useState<string[]>([]);
  const value = typeof row.value === 'string' ? row.value : String(row.value);

  // key-specific value autocomplete, debounced 300ms
  useEffect(() => {
    if (row.key === '*' || !value.trim()) {
      setOptions([]);
      return;
    }
    const t = setTimeout(() => {
      autocomplete(site, row.key, value.trim()).then(setOptions, () => setOptions([]));
    }, 300);
    return () => clearTimeout(t);
  }, [site, row.key, value]);

  return (
    <div className="adv-row">
      <button
        className={`adv-negate ${row.negate ? 'active' : ''}`}
        title={row.negate ? 'Negated (click to include)' : 'Negate this condition'}
        onClick={() => onChange({ ...row, negate: !row.negate })}
      >
        !
      </button>
      <select
        className="adv-row-key"
        value={row.key}
        onChange={(e) => onChange({ ...row, key: e.target.value, value: '' })}
      >
        <option value="*">all</option>
        {keys
          .filter((k) => k.id !== '*')
          .map((k) => (
            <option key={k.id} value={k.id}>
              {k.title ?? k.id}
            </option>
          ))}
      </select>
      <select
        className="adv-row-op"
        value={row.operator}
        onChange={(e) => onChange({ ...row, operator: e.target.value })}
      >
        {OPERATORS.map((op) => (
          <option key={op.value} value={op.value}>
            {op.label}
          </option>
        ))}
      </select>
      <input
        className="adv-row-value"
        list={listId}
        placeholder="value…"
        value={value}
        onChange={(e) => onChange({ ...row, value: e.target.value })}
      />
      <datalist id={listId}>
        {options.map((o) => (
          <option key={o} value={o} />
        ))}
      </datalist>
      <button className="ghost icon" title="Remove condition" onClick={onRemove}>
        ×
      </button>
    </div>
  );
}
